export function buildFailoverTargets(primary?: string | null, alt?: string | null): string[] {
  const targets: string[] = [];
  if (primary) targets.push(primary);
  if (alt && alt !== primary) targets.push(alt);
  return targets;
}

export function shouldRetryPlatformResponse(status: number): boolean {
  return status >= 500 || status === 408;
}

export async function fetchWithBaseFailover(
  targets: string[],
  path: string,
  init: RequestInit,
  options?: { label?: string; retryOnStatus?: (status: number) => boolean },
): Promise<Response> {
  let lastError: Error | null = null;
  let lastResponse: Response | null = null;
  const label = options?.label || path;
  const retryOnStatus = options?.retryOnStatus || shouldRetryPlatformResponse;

  for (let index = 0; index < targets.length; index += 1) {
    const base = targets[index];
    try {
      const response = await fetch(`${base}${path}`, init);
      if (!retryOnStatus(response.status) || index === targets.length - 1) {
        return response;
      }
      lastResponse = response;
      console.warn(`[platform-failover] ${label}: retrying alt target after ${response.status} from ${base}`);
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));
      if (index === targets.length - 1) break;
      console.warn(
        `[platform-failover] ${label}: primary target failed (${base}) — retrying alt: ${lastError.message}`,
      );
    }
  }

  if (lastResponse) return lastResponse;
  throw lastError || new Error(`All platform targets failed for ${label}`);
}

export function createPlatformFailoverClients(params: {
  platformInternalUrl?: string | null;
  platformInternalUrlAlt?: string | null;
  internalToken: string;
  appName: string;
}) {
  async function fetchPlatformInternal(path: string, init: RequestInit, label: string): Promise<Response> {
    const targets = buildFailoverTargets(params.platformInternalUrl, params.platformInternalUrlAlt);
    if (targets.length === 0) {
      throw new Error(`No platform internal URL configured for ${label}`);
    }
    return fetchWithBaseFailover(targets, path, init, { label });
  }

  function notifyGateway(payload: Record<string, unknown>): void {
    if (!params.platformInternalUrl || !params.appName) return;
    void fetchPlatformInternal(
      "/internal/realtime-notify",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Internal-Token": params.internalToken,
        },
        body: JSON.stringify({ appName: params.appName, ...payload }),
      },
      "realtime-notify",
    ).catch((e: Error) => {
      console.warn(`[realtime] Gateway notify failed:`, e.message);
    });
  }

  return { fetchPlatformInternal, notifyGateway };
}

export class RuntimeBuffer {
  private buf: Record<string, number> = {};

  add(key: string, value: number) {
    this.buf[key] = (this.buf[key] || 0) + value;
  }

  drain(): Record<string, number> {
    const snapshot = { ...this.buf };
    this.buf = {};
    return snapshot;
  }

  get isEmpty(): boolean {
    return Object.keys(this.buf).length === 0;
  }
}
